import { z } from "zod";
import { problemCodeSchema } from "./problem.js";
import { workbookIssueSchema } from "./workbook.js";
/*
 * Failed calculator uploads ("Planilhas com erro"). Every inspect, import or
 * repair-suggestions request that answers an error is kept so an administrator
 * can see what the operator saw and download the file that failed. The row id
 * is the support code the operator was shown ("Código para o suporte").
 */
/** Same shape as a sanitized transport id (X-Correlation-Id / X-Request-Id). */
export const uploadFailureIdSchema = z
    .string()
    .min(1)
    .max(128)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
export const uploadFailureStageSchema = z.enum(["INSPECT", "IMPORT", "REPAIR"]);
/** The real format of the bytes, from their magic number, not from the file name. */
export const uploadFileKindSchema = z.enum(["xlsx", "xls", "csv", "zip", "html", "unknown"]);
/** What the server found in the bytes, finer than the kind. */
export const uploadFormatFindingSchema = z.enum([
    /** An Office Open XML workbook (.xlsx). */
    "OOXML_WORKBOOK",
    /** A macro-enabled Office Open XML workbook (.xlsm). */
    "OOXML_MACRO_WORKBOOK",
    /** A password-protected (encrypted) .xlsx: an OLE2 container with an EncryptedPackage. */
    "OOXML_ENCRYPTED",
    /** A legacy Excel 97-2003 binary workbook (BIFF in OLE2). */
    "LEGACY_XLS_BIFF",
    /** Another OLE2 compound file (a .doc, an .msg). */
    "OLE2_OTHER",
    /** An HTML page or XML spreadsheet saved with a spreadsheet name (a typical ".xls" export). */
    "HTML_DISGUISED",
    /** A zip archive with no xl/workbook.xml. */
    "ZIP_WITHOUT_WORKBOOK",
    /** A zip signature whose central directory cannot be read. */
    "ZIP_DAMAGED",
    /** Delimited text (CSV). */
    "DELIMITED_TEXT",
    "PDF",
    "EMPTY",
    /** Text that is not a table. */
    "TEXT_UNKNOWN",
    "BINARY_UNKNOWN",
    /** The bytes never reached the server (the request was too large or carried no file). */
    "NOT_RECEIVED",
]);
export const uploadFormatReportSchema = z
    .object({
    kind: uploadFileKindSchema,
    finding: uploadFormatFindingSchema,
    /** MIME type of what the bytes are (not what the name says). */
    detectedContentType: z.string().min(1).max(120),
    /** Hex of the leading signature bytes, only for binary signatures (never text content). */
    magic: z
        .string()
        .regex(/^[0-9a-f]{0,32}$/)
        .nullable(),
    /** Lower-case extension of the file name, without the dot. */
    extension: z.string().max(20).nullable(),
    /** False when the name says one format and the bytes are another. */
    extensionMatches: z.boolean(),
    /** Worksheet names read from the workbook, when any could be read. */
    sheetNames: z.array(z.string().max(200)).max(60),
    /** Entries in the zip directory, for a zip. */
    zipEntries: z.number().int().nonnegative().nullable(),
})
    .strict();
/** What happened to the original bytes. */
export const uploadFailureOriginalStatusSchema = z.enum([
    /** Stored in the private bucket; downloadable by administrators until it expires. */
    "STORED",
    /** Being stored (the response did not wait for the upload). */
    "PENDING",
    /** The bytes never reached the server: metadata only. */
    "NOT_RECEIVED",
    /** Larger than the upload limit: metadata only. */
    "TOO_LARGE",
    /** Storage was unavailable: metadata only. */
    "STORE_FAILED",
    /** Past the retention window: deleted by the bucket lifecycle. */
    "EXPIRED",
]);
const violationSchema = z
    .object({
    field: z.string().min(1).max(200),
    message: z.string().min(1).max(500),
    issue: workbookIssueSchema.optional(),
})
    .strict();
export const uploadFailureSummarySchema = z
    .object({
    /** The support code the operator saw. */
    id: uploadFailureIdSchema,
    organizationId: z.string().uuid(),
    createdAt: z.string().datetime(),
    stage: uploadFailureStageSchema,
    httpStatus: z.number().int().min(400).max(599),
    problemCode: problemCodeSchema,
    /** The first workbook issue code, else the problem code. */
    errorCode: z.string().min(1).max(64),
    operator: z
        .object({
        principalId: z.string().uuid(),
        email: z.string().max(320).nullable(),
        displayName: z.string().max(200).nullable(),
    })
        .strict(),
    file: z
        .object({
        name: z.string().max(200).nullable(),
        sizeBytes: z.number().int().nonnegative().nullable(),
        sha256: z
            .string()
            .regex(/^[a-f0-9]{64}$/)
            .nullable(),
        kind: uploadFileKindSchema,
        finding: uploadFormatFindingSchema,
    })
        .strict(),
    original: z
        .object({
        status: uploadFailureOriginalStatusSchema,
        expiresAt: z.string().datetime().nullable(),
    })
        .strict(),
    review: z
        .object({
        reviewed: z.boolean(),
        reviewedAt: z.string().datetime().nullable(),
        reviewedBy: z.string().max(320).nullable(),
        note: z.string().max(2000).nullable(),
    })
        .strict(),
})
    .strict();
export const uploadFailureDetailSchema = uploadFailureSummarySchema
    .extend({
    /** The problem detail the API returned (English, for support). */
    detail: z.string().max(1000),
    violations: z.array(violationSchema).max(60),
    format: uploadFormatReportSchema,
    /** MIME type the file name implies. */
    declaredContentType: z.string().max(120).nullable(),
    requestId: z.string().max(128).nullable(),
    correlationId: z.string().max(128).nullable(),
    userAgent: z.string().max(400).nullable(),
    versions: z
        .object({
        app: z.string().max(120),
        engine: z.string().max(40),
        parser: z.string().max(40),
        ruleset: z.string().max(120).nullable(),
    })
        .strict(),
})
    .strict();
export const uploadFailurePageSchema = z
    .object({
    items: z.array(uploadFailureSummarySchema),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
})
    .strict();
export const UPLOAD_FAILURE_PAGE_MAX = 100;
export const uploadFailureQuerySchema = z
    .object({
    /** A support code, request id or correlation id (exact), or the start of one (8+ chars). */
    q: z.string().trim().min(1).max(128).optional(),
    /** First calendar day (inclusive), in America/Sao_Paulo. */
    from: z.string().date().optional(),
    /** Last calendar day (inclusive), in America/Sao_Paulo. */
    to: z.string().date().optional(),
    /** Operator principal id or e-mail (e-mail matches case-insensitively, by substring). */
    operator: z.string().trim().min(1).max(320).optional(),
    errorCode: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,63}$/)
        .optional(),
    kind: uploadFileKindSchema.optional(),
    stage: uploadFailureStageSchema.optional(),
    reviewed: z.enum(["true", "false"]).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.coerce.number().int().min(1).max(UPLOAD_FAILURE_PAGE_MAX).optional(),
})
    .strict();
export const uploadFailureReviewRequestSchema = z
    .object({
    reviewed: z.boolean(),
    note: z.string().trim().max(2000).nullable().optional(),
})
    .strict();
//# sourceMappingURL=calculation-upload-failures.js.map